import React, { useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { responseInterface } from 'swr';
import { HTTP_STATUS, ResponseError } from '../../../config/api';
import { DefaultPathLabel } from '../../../config/routes/paths';
import { useMeetingPrivateDetails } from '../../../hooks/api/protected/use-meeting-private-details';
import { useEventPublicInfo } from '../../../hooks/api/public/use-event-public-details';
import { useCurrentUserInfo } from '../../../hooks/api/protected/use-current-user-info';
import useDefaultRoutePage from '../../../hooks/use-default-route-page';
import { useMeetingId } from '../../../hooks/use-meeting-id';
import { useJoinMethodId } from '../../../hooks/use-join-method-id';
import { PrivateMeetingDetailsResponseType } from '../../../lib/api';
import { EventSettingsContextType, useEventSettings } from '../../../lib/context-providers/event-settings-context/event-settings-context';
import { EmbedContainer } from '../../../lib/kv-embed';
import { Connection } from '../../../lib/connection/connection';
import { JoinMethod } from '../../../lib/join-method/join-method';

type MeetingDetailsResponse = responseInterface<PrivateMeetingDetailsResponseType, ResponseError>;

const findKnovioEmbedCode = (availableKnovios: JoinMethod[], joinMethodId: string): string | undefined => {
  // Return the first available knovio embed code if the URL does not have a joinMethodId to support old path format "/stream/:meetingId/"
  return joinMethodId
    ? availableKnovios.find(joinMethod => joinMethod.joinMethodId === Number(joinMethodId))?.embedCodeId
    : availableKnovios[0].embedCodeId;
};

const renderKnovioEmbed = (joinMethods: JoinMethod[] | null | undefined, joinMethodId: string) => {
  const availableKnovios = joinMethods?.filter(joinMethod => joinMethod.isKnovioAvailable);
  if (!availableKnovios?.length) {
    return null;
  }

  const knovioEmbedCode = findKnovioEmbedCode(availableKnovios, joinMethodId);
  if (!knovioEmbedCode) {
    return null;
  }

  return <EmbedContainer.Knovio embedId={knovioEmbedCode} />;
};

const KnovioPageWrapper: React.FunctionComponent = (): JSX.Element | null => {
  const eventSettings = useEventSettings() as EventSettingsContextType;
  const { data: publicEventInfo } = useEventPublicInfo();
  const meetingId = useMeetingId();
  const joinMethodId = useJoinMethodId();
  const landingPage = useDefaultRoutePage();

  useCurrentUserInfo();

  const eventId = eventSettings?.eventId
    ? String(eventSettings.eventId)
    : publicEventInfo?.eventId
      ? String(publicEventInfo.eventId)
      : '';

  const { data, error } = useMeetingPrivateDetails(eventId, meetingId, joinMethodId) as MeetingDetailsResponse;

  const connection = data ? Connection.create(data) : null;
  const joinMethods = connection?.getJoinMethods();

  const meetingUnavailable = !!(error && (error.status === HTTP_STATUS.FORBIDDEN || error.status === HTTP_STATUS.NOT_FOUND));
  const hasRequestedKnovio = joinMethodId
    ? !!joinMethods?.some(
      joinMethod => joinMethod.isKnovioAvailable && joinMethod.joinMethodId === Number(joinMethodId),
    )
    : !!joinMethods?.some(joinMethod => joinMethod.isKnovioAvailable);
  const connectionIsNotApplicable = !!(data && !hasRequestedKnovio);

  useEffect(() => {
    if (data) {
      const presenterOrgName = data?.presenterOrganizations?.length ? data?.presenterOrganizations[0].name : '';
      const meetingData = data.topic
        ? data.topic
        : presenterOrgName;
      document.title = `${ DefaultPathLabel.CONNECTION_KNOVIO_PATH_LABEL } | ${ meetingData }`;
    }
  }, [data]);

  if (meetingUnavailable || connectionIsNotApplicable) return <Redirect to={landingPage} />;

  return renderKnovioEmbed(joinMethods, joinMethodId);
};

export default KnovioPageWrapper;
